import { Button, Card, Inline, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useSprint26FlagVisible } from "../lib/featureFlags.js";
import { useAdminNav } from "../lib/adminNav.js";

/**
 * Sprint 26 Wave B (Sprint 26.5) — Admin Quick Actions card.
 *
 * Read-only navigation shortcuts for the admin console overview.
 * Each shortcut deep-links to an existing read-only admin page via
 * the `useAdminNav()` context — no destructive actions, no
 * privileged infra calls, no secret/production actions.
 *
 * Why this is safe in Sprint 26.5:
 *   - Pure navigation. The card never invokes `fetch`; it only
 *     calls `navigateTo(<page>)` on the existing AdminNavProvider.
 *   - Every target is a read-only admin surface (Sprint 26 panel,
 *     Diagnostics, Smoke, Mail-log, Audit) — none expose write
 *     paths.
 *   - Gated by `sprint26.adminQuickActions` (`internal_admin_only`,
 *     default ON for admins). Flag flips off → the card is not
 *     rendered.
 */

interface QuickAction {
  readonly id: string;
  readonly target: string;
  readonly labelKey: string;
  readonly subtitleKey: string;
  /**
   * Optional Sprint 26 flag the target page is gated on. When the
   * flag is hidden the action is filtered out so we never render a
   * button that would just navigate to the OverviewPage fallback
   * (Codex P2 #340).
   */
  readonly gateFlag?: "sprint26.qaHarness";
}

const ACTIONS: readonly QuickAction[] = [
  {
    id: "sprint26",
    target: "sprint26",
    labelKey: "admin.quick_actions.sprint26.label",
    subtitleKey: "admin.quick_actions.sprint26.subtitle",
    // AdminApp falls back to OverviewPage when `sprint26.qaHarness`
    // is off, so this button would be a dead CTA in that combination
    // (operator disables qaHarness but keeps quick actions on).
    gateFlag: "sprint26.qaHarness",
  },
  {
    id: "diagnostics",
    target: "diagnostics",
    labelKey: "admin.quick_actions.diagnostics.label",
    subtitleKey: "admin.quick_actions.diagnostics.subtitle",
  },
  {
    id: "smoke",
    target: "smoke",
    labelKey: "admin.quick_actions.smoke.label",
    subtitleKey: "admin.quick_actions.smoke.subtitle",
  },
  {
    id: "audit",
    target: "audit",
    labelKey: "admin.quick_actions.audit.label",
    subtitleKey: "admin.quick_actions.audit.subtitle",
  },
  {
    id: "email_delivery",
    target: "email_delivery",
    labelKey: "admin.quick_actions.email_delivery.label",
    subtitleKey: "admin.quick_actions.email_delivery.subtitle",
  },
];

export function AdminQuickActionsCard() {
  const { t } = useLocale();
  const { visible } = useSprint26FlagVisible("sprint26.adminQuickActions");
  // Mirror AdminApp's qaHarness gate so the Sprint 26 panel button
  // is hidden when the panel itself is hidden.
  const { visible: qaHarnessVisible } = useSprint26FlagVisible("sprint26.qaHarness");
  const nav = useAdminNav();

  if (!visible) return null;

  const renderedActions = ACTIONS.filter((a) => {
    if (a.gateFlag === "sprint26.qaHarness") return qaHarnessVisible;
    return true;
  });

  return (
    <Card elevation={1} data-testid="admin-quick-actions-card">
      <Stack gap={3}>
        <Stack gap={0}>
          <strong style={{ fontSize: "var(--gt-sys-font-size-md)" }}>
            {t("admin.quick_actions.title")}
          </strong>
          <span
            style={{
              color: "var(--gt-sys-text-muted)",
              fontSize: "var(--gt-sys-font-size-xs)",
            }}
          >
            {t("admin.quick_actions.subtitle")}
          </span>
        </Stack>
        <Inline gap={2} wrap data-testid="admin-quick-actions-list">
          {renderedActions.map((a) => (
            <Button
              key={a.id}
              variant="secondary"
              onClick={() => nav.navigateTo(a.target)}
              data-testid={`admin-quick-actions-${a.id}`}
              title={t(a.subtitleKey)}
            >
              {t(a.labelKey)}
            </Button>
          ))}
        </Inline>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
        >
          {t("admin.quick_actions.read_only_note")}
        </span>
      </Stack>
    </Card>
  );
}
